// src/pages/Custom.jsx
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { WebLayout } from 'components/Layout';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Dialog, Transition, Disclosure } from '@headlessui/react';
import {
  Tool,
  Edit3,
  Image as ImageIcon,
  Calendar,
  DollarSign,
  MessageCircle,
  Instagram,
  Send,
  Clipboard,
  Check,
  X,
  AlertCircle,
  UploadCloud,
  Trash2,
} from 'react-feather';

const ORDER_LINKS = {
  whatsappNumber: '256782692271', // no plus sign
  instagram: 'https://instagram.com/gambit_games_ug',
  telegram: 'https://t.me/gambit_games_ug',
};

const GAME_OPTIONS = [
  { id: 'chess', label: 'Chess' },
  { id: 'ludo', label: 'Ludo (Family)' },
  { id: 'omweso', label: 'Omweso / Mancala' },
  { id: 'carrom', label: 'Carrom' },
  { id: 'dominoes', label: 'Dominoes' },
  { id: 'jenga', label: 'Jenga' },
  { id: 'backgammon', label: 'Backgammon' },
  { id: 'from-scratch', label: 'A New Game (from scratch)' },
];

const WOOD_OPTIONS = [
  { id: 'pine', label: 'Pine (Budget)' },
  { id: 'mugavu', label: 'Mugavu (Cordia africana)' },
  { id: 'mahogany', label: 'Mahogany' },
  { id: 'inlay', label: 'Mixed hardwood inlay' },
];

const SIZE_OPTIONS = [
  { id: 'travel', label: 'Travel / Compact' },
  { id: 'standard', label: 'Standard' },
  { id: 'large', label: 'Large / Showcase' },
];

const FINISH_OPTIONS = [
  { id: 'matte', label: 'Matte' },
  { id: 'satin', label: 'Satin' },
  { id: 'gloss', label: 'Gloss' },
];

function formatUGX(n) {
  try {
    return `UGX ${Number(n).toLocaleString('en-UG')}`;
  } catch {
    return `UGX ${n}`;
  }
}

// very rough, non-binding estimator for UX feedback only
function estimatePrice({ game, wood, size, quantity }) {
  const base = {
    chess: 220000,
    ludo: 90000,
    omweso: 160000,
    carrom: 300000,
    dominoes: 85000,
    jenga: 70000,
    backgammon: 240000,
    'from-scratch': 260000,
  }[game || 'from-scratch'];

  const woodMult = {
    pine: 0.9,
    mugavu: 1.0,
    mahogany: 1.25,
    inlay: 1.35,
  }[wood || 'mugavu'];

  const sizeMult = {
    travel: 0.8,
    standard: 1,
    large: 1.35,
  }[size || 'standard'];

  const qty = Math.max(1, Number(quantity) || 1);
  const single = base * woodMult * sizeMult;
  const subtotal = single * qty;
  const low = Math.round(subtotal * 0.95);
  const high = Math.round(subtotal * 1.15);
  return { low, high, single };
}

export function Custom() {
  // Parallax hero
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.85]);

  // Form state
  const [form, setForm] = useState({
    game: 'chess',
    wood: 'mugavu',
    size: 'standard',
    finish: 'satin',
    quantity: 1,
    engravingText: '',
    includeLogo: false,
    deadlineDays: 7,
    budgetNote: '',
    notes: '',
    contactPref: 'whatsapp', // whatsapp | instagram | telegram
  });

  // Reference images (local preview only)
  const [refs, setRefs] = useState([]); // [{file, url}]
  useEffect(() => {
    return () => {
      refs.forEach((r) => URL.revokeObjectURL(r.url));
    };
  }, []);

  const onPickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    const mapped = files.map((file) => ({
      file,
      url: URL.createObjectURL(file),
      name: file.name,
    }));
    setRefs((prev) => [...prev, ...mapped]);
    e.target.value = '';
  };
  const removeRef = (idx) => {
    const copy = [...refs];
    const [removed] = copy.splice(idx, 1);
    if (removed) URL.revokeObjectURL(removed.url);
    setRefs(copy);
  };

  const est = useMemo(
    () =>
      estimatePrice({
        game: form.game,
        wood: form.wood,
        size: form.size,
        quantity: form.quantity,
      }),
    [form.game, form.wood, form.size, form.quantity]
  );

  const briefText = useMemo(() => {
    const lines = [
      `GAMB1T CUSTOM ORDER REQUEST`,
      `Game: ${GAME_OPTIONS.find((g) => g.id === form.game)?.label}`,
      `Wood: ${WOOD_OPTIONS.find((w) => w.id === form.wood)?.label}`,
      `Size: ${SIZE_OPTIONS.find((s) => s.id === form.size)?.label}`,
      `Finish: ${FINISH_OPTIONS.find((f) => f.id === form.finish)?.label}`,
      `Quantity: ${form.quantity}`,
      form.engravingText ? `Engraving text: "${form.engravingText}"` : null,
      `Logo included: ${form.includeLogo ? 'Yes (see references)' : 'No'}`,
      `Preferred deadline: ${form.deadlineDays} day(s)`,
      form.budgetNote ? `Budget note: ${form.budgetNote}` : null,
      form.notes ? `Notes: ${form.notes}` : null,
      `Delivery: Kampala`,
      `----`,
      `Est. (non-binding): ${formatUGX(est.low)} - ${formatUGX(est.high)} (${formatUGX(
        est.single
      )} each)`,
      refs.length
        ? `Attached references (will share via chat): ${refs.map((r) => r.name).join(', ')}`
        : `References: none yet`,
    ].filter(Boolean);
    return lines.join('\n');
  }, [form, est, refs]);

  const [copied, setCopied] = useState(false);
  const copyBrief = async () => {
    try {
      await navigator.clipboard.writeText(briefText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  const encodedBrief = encodeURIComponent(briefText);
  const waHref = `https://wa.me/${ORDER_LINKS.whatsappNumber}?text=${encodedBrief}`;
  const instaHref = ORDER_LINKS.instagram;
  const tgHref = ORDER_LINKS.telegram;

  // UI helpers
  const chip = (active) =>
    `rounded-full px-3 py-1.5 text-xs ${
      active ? 'bg-[#12A2BF] text-black' : 'bg-gray-900 text-gray-200 hover:bg-gray-800'
    }`;

  return (
    <WebLayout>
      {/* HERO */}
      <section ref={heroRef} className="relative isolate overflow-hidden bg-gray-950">
        <motion.div style={{ y, opacity }} className="pointer-events-none absolute inset-0">
          <img
            src="/assets/custom_hero.jpg"
            alt=""
            className="h-full w-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-gray-950/60 via-gray-950/60 to-gray-950" />
        </motion.div>

        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#12A2BF]/30 bg-[#12A2BF]/10 px-3 py-1 text-xs font-medium text-[#12A2BF]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#12A2BF]" />
              Made in Uganda • Free delivery within Kampala.
            </div>
            <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              Design Your Custom Wooden Board Game
            </h1>
            <p className="mt-3 max-w-xl text-sm text-gray-300">
              Choose your base game, wood, size, engraving, and more. We’ll craft it to your taste
              and deliver within Kampala.
            </p>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to="/"
              className="rounded-full bg-gray-900 px-3 py-1.5 text-sm text-gray-200 hover:bg-gray-800"
            >
              ← Back to Shop
            </Link>
          </div>
        </div>
      </section>

      {/* FORM */}
      <section className="bg-gray-950">
        <div className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Left: form */}
            <form
              className="space-y-6 rounded-2xl border border-gray-800 bg-gray-900 p-6 lg:col-span-2"
              onSubmit={(e) => e.preventDefault()}
            >
              {/* Game */}
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-white">
                  <Tool size={16} /> Base Game
                </label>
                <div className="mt-3 flex flex-wrap gap-1">
                  {GAME_OPTIONS.map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, game: opt.id }))}
                      className={chip(form.game === opt.id)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Wood & Size */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-semibold text-white">Wood</label>
                  <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {WOOD_OPTIONS.map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, wood: opt.id }))}
                        className={chip(form.wood === opt.id)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white">Size</label>
                  <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
                    {SIZE_OPTIONS.map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, size: opt.id }))}
                        className={chip(form.size === opt.id)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Finish, Quantity */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-semibold text-white">Finish</label>
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    {FINISH_OPTIONS.map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, finish: opt.id }))}
                        className={chip(form.finish === opt.id)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white">Quantity</label>
                  <input
                    type="number"
                    min={1}
                    value={form.quantity}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, quantity: Math.max(1, Number(e.target.value || 1)) }))
                    }
                    className="mt-2 w-full rounded-md border border-gray-800 bg-gray-950 px-3 py-2 text-sm text-gray-100"
                  />
                </div>
              </div>

              {/* Engraving */}
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-white">
                  <Edit3 size={16} /> Engraving (optional)
                </label>
                <div className="mt-2 grid gap-3 sm:grid-cols-[1fr_auto]">
                  <input
                    type="text"
                    value={form.engravingText}
                    onChange={(e) => setForm((f) => ({ ...f, engravingText: e.target.value }))}
                    placeholder="e.g., The Kato Family, or Company Logo"
                    className="w-full rounded-md border border-gray-800 bg-gray-950 px-3 py-2 text-sm text-gray-100 placeholder-gray-500"
                  />
                  <label className="inline-flex items-center gap-2 text-sm text-gray-300">
                    <input
                      type="checkbox"
                      checked={form.includeLogo}
                      onChange={(e) => setForm((f) => ({ ...f, includeLogo: e.target.checked }))}
                      className="h-4 w-4 rounded border-gray-700 bg-gray-900 text-[#12A2BF]"
                    />
                    Include logo
                  </label>
                </div>

                {/* simple preview */}
                <div className="mt-3 rounded-lg border border-gray-800 bg-gray-950 p-4">
                  <p className="text-xs text-gray-400">Engraving preview (approximate)</p>
                  <div className="mt-2 flex h-24 items-center justify-center rounded-md border border-dashed border-gray-800 bg-gray-900">
                    <span className="select-none text-sm tracking-wide text-gray-200">
                      {form.engravingText || '—'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Deadline & Budget */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-white">
                    <Calendar size={16} /> Desired Deadline (days)
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={form.deadlineDays}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        deadlineDays: Math.max(1, Number(e.target.value || 1)),
                      }))
                    }
                    className="mt-2 w-full rounded-md border border-gray-800 bg-gray-950 px-3 py-2 text-sm text-gray-100"
                  />
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-white">
                    <DollarSign size={16} /> Budget Note (optional)
                  </label>
                  <input
                    type="text"
                    value={form.budgetNote}
                    onChange={(e) => setForm((f) => ({ ...f, budgetNote: e.target.value }))}
                    placeholder="e.g., targeting ~ UGX 200k"
                    className="mt-2 w-full rounded-md border border-gray-800 bg-gray-950 px-3 py-2 text-sm text-gray-100 placeholder-gray-500"
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-semibold text-white">Notes (optional)</label>
                <textarea
                  rows={4}
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  placeholder="Tell us about colors, themes, who it’s for, etc."
                  className="mt-2 w-full rounded-md border border-gray-800 bg-gray-950 px-3 py-2 text-sm text-gray-100 placeholder-gray-500"
                />
              </div>

              {/* Reference images */}
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-white">
                  <ImageIcon size={16} /> Reference Images (optional)
                </label>
                <p className="mt-1 text-xs text-gray-400">
                  Add sketches or inspiration. These won’t upload here—just for your preview; share
                  them when we chat.
                </p>
                <div className="mt-3 flex items-center gap-3">
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-gray-200 hover:bg-gray-800">
                    <UploadCloud size={16} />
                    Add images
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      multiple
                      onChange={onPickFiles}
                    />
                  </label>
                  {refs.length > 0 && (
                    <span className="text-xs text-gray-400">{refs.length} selected</span>
                  )}
                </div>

                {refs.length > 0 && (
                  <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                    {refs.map((r, idx) => (
                      <div
                        key={idx}
                        className="group relative overflow-hidden rounded-lg border border-gray-800"
                      >
                        <img src={r.url} alt={r.name} className="h-36 w-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removeRef(idx)}
                          className="absolute right-1 top-1 hidden rounded bg-black/60 p-1 text-white group-hover:block"
                          aria-label="Remove image"
                        >
                          <Trash2 size={14} />
                        </button>
                        <div className="truncate px-2 py-1 text-[11px] text-gray-300">{r.name}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </form>

            {/* Right: summary & actions */}
            <aside className="space-y-6">
              <div className="rounded-2xl border border-gray-800 bg-gray-900 p-6">
                <h3 className="text-sm font-semibold text-white">Order Summary</h3>
                <dl className="mt-3 space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <dt className="text-gray-400">Game</dt>
                    <dd className="text-gray-200">
                      {GAME_OPTIONS.find((g) => g.id === form.game)?.label}
                    </dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-gray-400">Wood</dt>
                    <dd className="text-gray-200">
                      {WOOD_OPTIONS.find((w) => w.id === form.wood)?.label}
                    </dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-gray-400">Size</dt>
                    <dd className="text-gray-200">
                      {SIZE_OPTIONS.find((s) => s.id === form.size)?.label}
                    </dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-gray-400">Finish</dt>
                    <dd className="text-gray-200">
                      {FINISH_OPTIONS.find((f) => f.id === form.finish)?.label}
                    </dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-gray-400">Quantity</dt>
                    <dd className="text-gray-200">{form.quantity}</dd>
                  </div>
                  {form.engravingText && (
                    <div className="flex items-center justify-between">
                      <dt className="text-gray-400">Engraving</dt>
                      <dd className="text-gray-200 max-w-[60%] truncate">“{form.engravingText}”</dd>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <dt className="text-gray-400">Logo</dt>
                    <dd className="text-gray-200">{form.includeLogo ? 'Yes' : 'No'}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-gray-400">Deadline</dt>
                    <dd className="text-gray-200">{form.deadlineDays} day(s)</dd>
                  </div>
                </dl>

                <div className="mt-4 rounded-lg border border-gray-800 bg-gray-950 p-4">
                  <p className="text-xs text-gray-400">Estimated (non-binding)</p>
                  <p className="mt-1 text-lg font-semibold text-[#12A2BF]">
                    {formatUGX(est.low)} – {formatUGX(est.high)}
                  </p>
                  <p className="text-xs text-gray-500">
                    ~ {formatUGX(est.single)} each (final quote after we review your brief &
                    references).
                  </p>
                </div>

                <div className="mt-4 grid gap-2 sm:grid-cols-3">
                  <a
                    href={waHref}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-[#12A2BF] px-3 py-2 text-sm font-semibold text-black hover:brightness-110"
                  >
                    <MessageCircle size={16} />
                    WhatsApp
                  </a>
                  <a
                    href={instaHref}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-pink-500/90 px-3 py-2 text-sm font-semibold text-white hover:brightness-110"
                  >
                    <Instagram size={16} />
                    Instagram
                  </a>
                  <a
                    href={tgHref}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-sky-500 px-3 py-2 text-sm font-semibold text-white hover:brightness-110"
                  >
                    <Send size={16} />
                    Telegram
                  </a>
                </div>

                <button
                  onClick={copyBrief}
                  className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-md border border-gray-700 px-3 py-2 text-sm text-gray-200 hover:bg-gray-800"
                >
                  <Clipboard size={16} />
                  {copied ? (
                    <span className="inline-flex items-center gap-1 text-[#12A2BF]">
                      Copied <Check size={14} />
                    </span>
                  ) : (
                    'Copy brief'
                  )}
                </button>

                <p className="mt-3 flex items-start gap-2 text-xs text-gray-400">
                  <AlertCircle size={14} className="mt-0.5" />
                  This estimate helps planning. Final pricing, timeline, and payment terms are
                  confirmed with our team on chat.
                </p>
              </div>

              {/* Inspiration / examples */}
              <div className="rounded-2xl border border-gray-800 bg-gray-900 p-6">
                <h3 className="text-sm font-semibold text-white">Recent Custom Work</h3>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <img
                    src="/assets/custom/inlay_chess.jpg"
                    alt="Inlay chess"
                    className="h-28 w-full rounded-lg object-cover"
                  />
                  <img
                    src="/assets/custom/engraved_ludo.jpg"
                    alt="Engraved Ludo"
                    className="h-28 w-full rounded-lg object-cover"
                  />
                  <img
                    src="/assets/custom/mugavu_omweso.jpg"
                    alt="Mugavu Omweso"
                    className="h-28 w-full rounded-lg object-cover"
                  />
                  <img
                    src="/assets/custom/corporate_bundle.jpg"
                    alt="Corporate bundle"
                    className="h-28 w-full rounded-lg object-cover"
                  />
                </div>
                <p className="mt-3 text-xs text-gray-400">
                  We build per request — names, logos, size tweaks, unique mechanics, and more.
                </p>
              </div>
            </aside>
          </div>

          {/* FAQ */}
          <div className="mt-10 rounded-2xl border border-gray-800 bg-gray-900 p-6">
            <h3 className="text-sm font-semibold text-white">FAQs</h3>
            <div className="mt-3 divide-y divide-gray-800">
              {[
                {
                  q: 'How long do custom orders take?',
                  a: 'Most projects take 3–7 days depending on complexity and finishing time. We’ll confirm your timeline after reviewing your brief.',
                },
                {
                  q: 'Do you deliver outside Kampala?',
                  a: 'Right now we deliver within Kampala. For special cases, chat with us and we’ll advise.',
                },
                {
                  q: 'Can you engrave my logo?',
                  a: 'Yes. Add “Include logo” and share your logo file (PNG/SVG) when we chat.',
                },
                {
                  q: 'What woods do you use?',
                  a: 'We love Mugavu and Mahogany. We also make budget builds in Pine and premium inlay boards mixing hardwoods.',
                },
              ].map((item, i) => (
                <Disclosure as="div" key={i} className="py-3">
                  {({ open }) => (
                    <>
                      <Disclosure.Button className="flex w-full items-center justify-between py-2 text-left text-sm text-gray-200 hover:text-white">
                        <span>{item.q}</span>
                        <X
                          className={`h-4 w-4 transition-transform ${
                            open ? 'rotate-45' : 'rotate-0'
                          }`}
                        />
                      </Disclosure.Button>
                      <Disclosure.Panel className="pr-6 text-sm text-gray-400">
                        {item.a}
                      </Disclosure.Panel>
                    </>
                  )}
                </Disclosure>
              ))}
            </div>
          </div>
        </div>
      </section>
    </WebLayout>
  );
}
